// cardOrder.js – Reihenfolge der Karten (Vorschau und Druck) und Extrakarten-Plan
// Zuerst die mehrfarbigen Karten (oberste Generation), dann jede Familie (Farbe) nach Alter.
import { computeLayout } from './treeLayout';
import { cheapestPacks } from './pricing';
import { planExtras } from './extraCards';

// Ältere zuerst; ohne Geburtsdatum ans Ende der Gruppe
// Vergleichsfunktion für .sort(): ältere Personen zuerst. Geburtsdaten haben das
// Format JJJJ-MM-TT, darum kann man sie als Text vergleichen. Bei gleichem oder
// fehlendem Datum entscheidet die Position im Stammbaum (links vor rechts).
function byAge(pos) {
  return (a, b) => {
    const da = a.birthDate || '', db = b.birthDate || '';
    if (da && db && da !== db) return da < db ? -1 : 1;
    if (!da !== !db) return da ? -1 : 1;
    return (pos.get(a.id)?.x ?? 0) - (pos.get(b.id)?.x ?? 0);
  };
}

/**
 * Sortiert die Personen für die Karten.
 * colors: Ergebnis von computeColorSystem
 * Rückgabe: { ordered, familyColors }
 */
export function orderPersonsForCards(persons, relationships, colors) {
  // Positionen im Stammbaum (nur für die Sortierung bei gleichem Alter).
  const pos = computeLayout(persons, relationships);
  const sort = byAge(pos);
  // Drei Gruppen: mehrfarbige Karten (oberste Generation), Karten pro Familienfarbe, Rest.
  const multi = [], byColor = new Map(), rest = [];
  persons.forEach(p => {
    const ci = colors.get(p.id);
    if (ci?.color) {
      if (!byColor.has(ci.color)) byColor.set(ci.color, []);
      byColor.get(ci.color).push(p);
    } else if (ci?.borderColors?.length) multi.push(p);
    else rest.push(p);
  });
  // Familien in der Reihenfolge, in der ihre Farben auf den mehrfarbigen Karten vorkommen
  const familyColors = [];
  multi.sort(sort).forEach(p => (colors.get(p.id).borderColors || []).forEach(c => {
    if (byColor.has(c) && !familyColors.includes(c)) familyColors.push(c);
  }));
  [...byColor.keys()].forEach(c => { if (!familyColors.includes(c)) familyColors.push(c); });

  // Zusammensetzen: mehrfarbige, dann jede Familie (nach Alter), dann der Rest.
  const ordered = [...multi];
  familyColors.forEach(c => ordered.push(...byColor.get(c).sort(sort)));
  ordered.push(...rest.sort(sort));
  return { ordered, familyColors };
}

// Karten, die gedruckt werden. onlyNew = Bestellung als Erweiterung: die aus dem
// Original übernommenen Personen (colors → locked) sind schon gedruckt und fallen weg;
// es zählen dann auch nur die Familienfarben der neuen Karten (für die Leerpersonen).
export function cardsToPrint(persons, relationships, colors, onlyNew = false) {
  const res = orderPersonsForCards(persons, relationships, colors);
  if (!onlyNew) return res;
  const ordered = res.ordered.filter(p => !colors.get(p.id)?.locked);
  const present = new Set(ordered.map(p => colors.get(p.id)?.color).filter(Boolean));
  return { ordered, familyColors: res.familyColors.filter(c => present.has(c)) };
}

// Extrakarten für die Schachtel(n) dieses Stammbaumes
// Wie viele Personen passen in die Schachtel(n), und welche Extrakarten kommen dazu?
// Die Regeln selbst stehen in extraCards.js (planExtras).
export function extraPlanFor(personCount, familyColors) {
  const { packs } = cheapestPacks(personCount);
  const capacity = packs.reduce((s, p) => s + p.size, 0);
  return { packs, capacity, ...planExtras({ persons: personCount, capacity, familyColors }) };
}
